import { AbacusAssessmentView, AbacusLevelDTO, AbacusTopicCardDTO } from '../services/abacusService';

/** Junior or Senior from the enrolled course programme. Unknown values stay as stored. */
export function trackBadgeLabel(track: string | null | undefined): string {
  if (!track || !track.trim()) {
    return 'Abacus';
  }
  const normalized = track.trim().toUpperCase();
  if (normalized === 'JUNIOR') return 'Junior';
  if (normalized === 'SENIOR') return 'Senior';
  return track.trim();
}

/** Short category names for the course-home tree. Unknown keys keep the API title. */
export function categoryLabel(key: string, fallback: string): string {
  if (key === 'ABACUS_FINGER_THEORY') return 'Theory';
  if (key === 'ABACUS') return 'Abacus';
  if (key === 'MENTAL_THEORY') return 'Mental';
  return fallback?.trim() ? fallback : 'Topics';
}

export function isCurrentLevel(levelCode: string, currentLevelCode: string | null | undefined): boolean {
  return Boolean(currentLevelCode) && levelCode === currentLevelCode;
}

/** Requirement text already returned for a locked level. Null when the level is open. */
export function levelLockReason(level: Pick<AbacusLevelDTO, 'unlocked' | 'categories'>): string | null {
  if (level.unlocked) return null;
  for (const category of level.categories) {
    for (const topic of category.topics) {
      if (topic.lockReason) return topic.lockReason;
    }
  }
  return 'Complete previous level to unlock';
}

export function practiceSummary(
  topic: Pick<
    AbacusTopicCardDTO,
    | 'practiceAttempts'
    | 'lastScorePercent'
    | 'contentMode'
    | 'isTestWeek'
    | 'assessmentKind'
    | 'minPassPercent'
    | 'maxAttempts'
    | 'attemptsUsed'
    | 'lastPassed'
  >
): string {
  if (topic.contentMode === 'THEORY') {
    const attempts = topic.practiceAttempts ?? 0;
    const noun = attempts === 1 ? 'practice attempt' : 'practice attempts';
    const base = `${attempts} ${noun}`;
    if (topic.lastScorePercent == null) return base;
    return `${base} · Last score ${topic.lastScorePercent}%`;
  }
  if (topic.isTestWeek || topic.assessmentKind === 'TEST_WEEK') {
    const pass = topic.minPassPercent ?? 80;
    const used = topic.attemptsUsed ?? 0;
    const max = topic.maxAttempts ?? 3;
    const outcome = topic.lastPassed === true ? 'Passed' : topic.lastPassed === false ? 'Not passed' : null;
    const base = `Test Week · Pass mark ${pass}% · ${used} of ${max} attempts`;
    return outcome ? `${base} · ${outcome}` : base;
  }
  const pass = topic.minPassPercent ?? 70;
  const attempts = topic.practiceAttempts ?? 0;
  const noun = attempts === 1 ? 'practice attempt' : 'practice attempts';
  const base = `${attempts} ${noun} · Feedback at ${pass}%`;
  if (topic.lastScorePercent == null) return base;
  return `${base} · Last score ${topic.lastScorePercent}%`;
}

/** Drill heading: practice feedback or Test Week pass mark and attempt count. */
export function assessmentRuleText(
  view: Pick<AbacusAssessmentView, 'assessmentKind' | 'minPassPercent' | 'maxAttempts' | 'attemptsUsed'>
): string {
  const testWeek = view.assessmentKind === 'TEST_WEEK';
  const pass = view.minPassPercent ?? (testWeek ? 80 : 70);
  if (!testWeek) {
    if (view.maxAttempts == null) {
      return `Practice · Feedback at ${pass}% · retries unlimited`;
    }
    return `Practice · Feedback at ${pass}% · ${view.attemptsUsed ?? 0} of ${view.maxAttempts} attempts`;
  }
  return `Test Week · Pass mark ${pass}% · ${view.attemptsUsed ?? 0} of ${view.maxAttempts ?? 3} attempts`;
}
